import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
  setThemeTransient: (theme: Theme) => void;
  resetTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  theme: 'dark',
  toggleTheme: () => {},
  setTheme: () => {},
  setThemeTransient: () => {},
  resetTheme: () => {},
});

function applyThemeToDom(theme: Theme) {
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
}

function readSavedTheme(): Theme {
  try {
    // Clean up old key from previous builds that caused logout pollution
    localStorage.removeItem('theme');
    const saved = localStorage.getItem('app-theme');
    return saved === 'light' || saved === 'dark' ? saved : 'dark';
  } catch {
    return 'dark';
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Initialize from localStorage synchronously so first render matches the
  // blocking script in index.html — prevents any React-level flash
  const [theme, setThemeState] = useState<Theme>(readSavedTheme);

  useEffect(() => {
    // Sync DOM on mount in case the blocking script and React disagree
    applyThemeToDom(theme);
  }, []);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    try {
      localStorage.setItem('app-theme', newTheme);
    } catch { /* ignore */ }
    applyThemeToDom(newTheme);
  };

  // Sets the DOM theme without persisting to localStorage — use in portals
  // so that the homepage dark theme preference is not overwritten.
  const setThemeTransient = (newTheme: Theme) => {
    setThemeState(newTheme);
    applyThemeToDom(newTheme);
  };

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  // Called on logout — resets to dark and wipes saved preference
  const resetTheme = () => {
    try {
      localStorage.removeItem('app-theme');
      localStorage.removeItem('theme');
      localStorage.removeItem('workday-client-theme');
    } catch { /* ignore */ }
    setThemeState('dark');
    applyThemeToDom('dark');
    // Also clear the workday client theme attribute
    document.documentElement.removeAttribute('data-workday-client-theme');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme, setThemeTransient, resetTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
